// client/src/components/indicadores/PagamentosReport.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // ✅ USANDO API PADRONIZADA
import useAlert from '../../hooks/useAlert';
import { useAuth } from '../../context/auth/AuthProvider';
import { usePagamentosReport } from '../../hooks/useReports';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';
import { CreditCard, DollarSign, PieChart, TrendingUp, BarChart3 } from 'lucide-react';
import ReportPageLayout from './ReportPageLayout';
import ReportTable from './ReportTable';
import DoughnutChart from '../dashboard/DoughnutChart';
import KpiCard from '../dashboard/KpiCard';

// ✅ DEFINIR FILTROS INICIAIS COMO CONSTANTE
const initialFilters = {
  startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  endDate: new Date(),
};

const PagamentosReport = () => {
    const { setAlert } = useAlert();
    const { user, scope } = useAuth();
    // ✅ ACHADO: relatorio fixo em BRL, sem ler configuracoesGerais.i18n
    // da conta.
    const { currency, locale } = useTenantI18n();

    // ✅ INICIAR COM FILTROS VÁLIDOS, NUNCA NULL
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [filterOptions, setFilterOptions] = useState({
        unidades: [],
        profissionais: [],
        servicos: [],
        recursos: [],
        metodosPagamento: []
    });
    const [loadingFilters, setLoadingFilters] = useState(false);

    // 🔥 USANDO NOVO HOOK - usePagamentosReport
    const { 
        data: reportData = [],
        isLoading,
        error 
    } = usePagamentosReport(appliedFilters);

    // ✅ TRATAMENTO DE ERRO MELHORADO
    useEffect(() => {
        if (error) {
            console.error('❌ Erro no relatório de pagamentos:', error);
            
            let errorMessage = 'Ocorreu um erro ao buscar o relatório de formas de pagamento.';
            
            if (error.message.includes('Servidor indisponível')) {
                errorMessage = error.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setAlert(errorMessage, 'danger');
        }
    }, [error, setAlert]);

    // 🔥 CARREGAR OPÇÕES DE FILTRO - PADRONIZADO COM OUTROS COMPONENTES
    useEffect(() => {
        const fetchFilterOptions = async () => {
            // ✅ CORREÇÃO: só bloqueava sem user.account — franquia_admin e
            // matriz_admin nunca viam essas opções de filtro. Os endpoints
            // já escopam sozinhos pelo JWT (filterByUserUnits).
            if (!user?.account?._id && !user?.franquia?._id && scope !== 'matriz') {
                console.log('⚠️ Usuário sem conta, franquia ou escopo matriz — sem opções de filtro');
                return;
            }

            setLoadingFilters(true);

            try {
                const accountId = user?.account?._id;
                const baseParams = accountId ? { account: accountId, limit: 1000 } : { limit: 1000 };
                console.log('🔄 Carregando filtros de pagamentos', baseParams);

                // ✅ PADRONIZADO: Usando a mesma estrutura dos outros componentes
                const requests = [
                    // Unidades
                    api.get('/unidades', { params: baseParams }),
                    // Profissionais
                    api.get('/users', { params: { ...baseParams, role: 'profissional', isActive: true } }),
                    // Serviços
                    api.get('/servicos', { params: baseParams }),
                    // Recursos
                    api.get('/recursos', { params: { ...baseParams, status: 'Ativo' } }),
                    // Métodos de Pagamento
                    api.get('/metodos-pagamento', { params: baseParams })
                ];

                console.log('📡 Fazendo requisições para filtros...');
                
                const [
                    unidadesRes, 
                    profissionaisRes, 
                    servicosRes, 
                    recursosRes, 
                    metodosPagamentoRes
                ] = await Promise.all(requests);

                // ✅ PADRONIZADO: Mesma estrutura de extração de dados
                const filterData = {
                    unidades: unidadesRes.data?.data || unidadesRes.data || [],
                    profissionais: profissionaisRes.data?.data || profissionaisRes.data || [],
                    servicos: servicosRes.data?.data || servicosRes.data || [],
                    recursos: recursosRes.data?.data || recursosRes.data || [],
                    metodosPagamento: metodosPagamentoRes.data?.data || metodosPagamentoRes.data || []
                };

                setFilterOptions(filterData);

                console.log('✅ Filtros de pagamentos carregados com sucesso:', {
                    unidades: filterData.unidades.length,
                    profissionais: filterData.profissionais.length,
                    servicos: filterData.servicos.length,
                    recursos: filterData.recursos.length,
                    metodosPagamento: filterData.metodosPagamento.length
                });

            } catch (error) {
                console.error('❌ Erro ao carregar filtros de pagamentos:', error);
                
                let errorMessage = 'Erro ao carregar opções de filtro.';
                
                if (error.message.includes('Servidor indisponível')) {
                    errorMessage = 'Servidor backend indisponível. Verifique se está rodando.';
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setAlert(errorMessage, 'danger');
                
                // ✅ FALLBACK: Definir arrays vazios para evitar quebra da UI
                setFilterOptions({
                    unidades: [],
                    profissionais: [],
                    servicos: [],
                    recursos: [],
                    metodosPagamento: []
                });
            } finally {
                setLoadingFilters(false);
            }
        };
        
        fetchFilterOptions();
    }, [user, scope, setAlert]);

    // ✅ FUNÇÃO PARA APLICAR FILTROS (GARANTINDO QUE NÃO SEJA NULL)
    const handleApplyFilters = (filters) => {
        console.log('🔄 Aplicando filtros:', filters);
        setAppliedFilters(filters || initialFilters);
    };

    // 🔥 KPIs DINÂMICOS COM VALORES PADRÃO SEGUROS
    const kpiCards = [
        {
            title: "Faturamento Total",
            value: formatCurrency(reportData?.reduce((sum, item) => sum + (item.total || 0), 0) || 0, currency, locale),
            prefix: "",
            icon: DollarSign,
            description: "Valor total recebido"
        },
        { 
            title: "Transações Realizadas", 
            value: (reportData?.reduce((sum, item) => sum + (item.count || 0), 0) || 0).toLocaleString('pt-BR'), 
            prefix: "",
            icon: CreditCard,
            description: "Total de transações de pagamento"
        },
        { 
            title: "Formas de Pagamento", 
            value: (reportData?.length || 0).toLocaleString('pt-BR'), 
            prefix: "",
            icon: PieChart,
            description: "Métodos de pagamento utilizados"
        },
        {
            title: "Ticket Médio",
            value: formatCurrency(reportData?.length > 0
                ? (reportData.reduce((sum, item) => sum + (item.total || 0), 0) / reportData.reduce((sum, item) => sum + (item.count || 0), 1))
                : 0, currency, locale),
            prefix: "",
            icon: TrendingUp,
            description: "Valor médio por transação"
        }
    ];

    // 🔥 DADOS PARA GRÁFICO DE PIZZA
    const getChartData = () => {
        if (!reportData || reportData.length === 0) return null;

        return {
            labels: reportData.map(item => item.nome || 'Não especificado'),
            datasets: [
                {
                    data: reportData.map(item => item.total || 0),
                    backgroundColor: [
                        '#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6',
                        '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1',
                        '#14b8a6', '#f43f5e', '#8b5cf6', '#06b6d4', '#84cc16'
                    ],
                    borderWidth: 2,
                    borderColor: '#fff'
                },
            ],
        };
    };

    const chartData = getChartData();

    // 🔥 CONFIGURAÇÕES DAS TABELAS
    const tableConfigurations = [
        {
            title: "Distribuição por Forma de Pagamento",
            columns: [
                { 
                    key: 'nome', 
                    header: 'Forma de Pagamento', 
                    render: (row) => (
                        <div className="d-flex align-items-center">
                            <CreditCard size={16} className="me-2 text-muted" />
                            <span>{row.nome || 'Não especificado'}</span>
                            {row.tipo && (
                                <span className={`badge ms-2 ${
                                    row.tipo === 'PRE_PAGO' ? 'bg-primary' : 'bg-success'
                                }`}>
                                    {row.tipo === 'PRE_PAGO' ? 'Pré-pago' : 'Pós-pago'}
                                </span>
                            )}
                        </div>
                    ),
                    sortable: true
                },
                { 
                    key: 'count', 
                    header: 'Transações', 
                    render: (row) => (
                        <span className="fw-semibold">
                            {(row.count || 0).toLocaleString('pt-BR')}
                        </span>
                    ),
                    sortable: true
                },
                { 
                    key: 'numeroVendas', 
                    header: 'Vendas', 
                    render: (row) => (
                        <span className="text-muted">
                            {(row.numeroVendas || 0).toLocaleString('pt-BR')}
                        </span>
                    ),
                    sortable: true
                },
                { 
                    key: 'total', 
                    header: 'Valor Total', 
                    render: (row) => (
                        <span className="fw-bold text-success">
                            {formatCurrency(row.total || 0, currency, locale)}
                        </span>
                    ),
                    sortable: true
                },
                { 
                    key: 'percentual', 
                    header: 'Participação', 
                    render: (row) => {
                        const totalGeral = reportData?.reduce((sum, item) => sum + (item.total || 0), 0) || 1;
                        const percentual = ((row.total || 0) / totalGeral) * 100;
                        return (
                            <span className={`fw-semibold ${
                                percentual >= 30 ? 'text-success' : 
                                percentual >= 15 ? 'text-primary' : 'text-muted'
                            }`}>
                                {percentual.toFixed(1)}%
                            </span>
                        );
                    },
                    sortable: true
                },
                { 
                    key: 'ticketMedio', 
                    header: 'Ticket Médio', 
                    render: (row) => (
                        <span className="fw-semibold">
                            {formatCurrency(row.ticketMedio || 0, currency, locale)}
                        </span>
                    ),
                    sortable: true
                },
            ],
            data: reportData || [],
            defaultSort: 'total',
            defaultSortDirection: 'desc'
        }
    ];

    // 🔥 ESTATÍSTICAS ADICIONAIS
    const getPaymentStats = () => {
        if (!reportData || reportData.length === 0) return null;

        const totalFaturamento = reportData.reduce((sum, item) => sum + (item.total || 0), 0);
        const totalTransacoes = reportData.reduce((sum, item) => sum + (item.count || 0), 0);
        const totalVendas = reportData.reduce((sum, item) => sum + (item.numeroVendas || 0), 0);
        
        // Método mais popular por quantidade de transações
        const metodoMaisPopular = reportData.reduce((prev, current) => 
            (prev.count > current.count) ? prev : current
        );

        // Método com maior faturamento
        const metodoMaiorFaturamento = reportData.reduce((prev, current) => 
            (prev.total > current.total) ? prev : current
        );

        // Método com maior ticket médio
        const metodoMaiorTicket = reportData.reduce((prev, current) => {
            const prevTicket = prev.ticketMedio || 0;
            const currentTicket = current.ticketMedio || 0;
            return prevTicket > currentTicket ? prev : current;
        });

        // Distribuição por tipo
        const prePagos = reportData.filter(item => item.tipo === 'PRE_PAGO');
        const posPagos = reportData.filter(item => item.tipo === 'POS_PAGO');

        return {
            totalFaturamento,
            totalTransacoes,
            totalVendas,
            metodoMaisPopular,
            metodoMaiorFaturamento,
            metodoMaiorTicket,
            distribuicaoTipos: {
                prePagos: prePagos.length,
                posPagos: posPagos.length,
                faturamentoPrePago: prePagos.reduce((sum, item) => sum + (item.total || 0), 0),
                faturamentoPosPago: posPagos.reduce((sum, item) => sum + (item.total || 0), 0)
            },
            ticketMedioGeral: totalTransacoes > 0 ? totalFaturamento / totalTransacoes : 0
        };
    };

    const paymentStats = getPaymentStats();

    // ✅ VERIFICAÇÃO DE CONEXÃO COM BACKEND
    const isBackendUnavailable = error?.message?.includes('Servidor indisponível');

    // ✅ LOADING STATE MELHORADO
    if (loadingFilters) {
        return (
            <div className="content-panel">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="mt-3 text-muted">Carregando opções de filtro...</p>
                </div>
            </div>
        );
    }

    return (
        <ReportPageLayout
            icon={<CreditCard size={24} />}
            title="Relatório de Vendas por Forma de Pagamento"
            onApplyFilters={handleApplyFilters}
            filterOptions={filterOptions}
            enabledFilters={['date', 'profissional', 'unidade', 'metodoPagamento']}
            isLoading={isLoading || loadingFilters}
            kpiCards={kpiCards}
            tableConfigurations={tableConfigurations}
            initialFilters={initialFilters}
        >
            {/* 🔥 MENSAGEM DE ERRO DE CONEXÃO - PRIORIDADE MÁXIMA */}
            {isBackendUnavailable && (
                <div className="content-panel mb-4">
                    <div className="alert alert-danger">
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <h5>❌ Erro de Conexão com o Servidor</h5>
                                <p className="mb-2">{error.message}</p>
                                <small className="text-muted">
                                    <strong>Para resolver:</strong><br/>
                                    1. Verifique se o servidor backend está rodando<br/>
                                    2. Confirme se a API está respondendo<br/>
                                    3. Verifique o comando: <code>npm run dev</code> no backend<br/>
                                    4. Recarregue a página após iniciar o servidor
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔥 TABELA DE DETALHE — tableConfigurations era montado mas
                nunca renderizado (ReportPageLayout não usa essa prop, só
                `children`), então a tabela nunca aparecia na tela. */}
            {!isBackendUnavailable && tableConfigurations.map((config) => (
                <ReportTable
                    key={config.title}
                    title={config.title}
                    columns={config.columns}
                    data={config.data}
                    isLoading={isLoading}
                />
            ))}

            {/* 🔥 GRÁFICO DE DISTRIBUIÇÃO */}
            {!isBackendUnavailable && chartData && paymentStats && (
                <div className="content-panel mb-4">
                    <div className="row">
                        <div className="col-lg-6">
                            <h4 className="panel-title">Distribuição do Faturamento</h4>
                            <div className="chart-container" style={{ height: '320px' }}>
                                <DoughnutChart 
                                    title="Por Forma de Pagamento"
                                    data={chartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: {
                                                position: 'right',
                                                labels: {
                                                    usePointStyle: true,
                                                    padding: 15,
                                                    font: {
                                                        size: 12
                                                    }
                                                }
                                            },
                                            tooltip: {
                                                callbacks: {
                                                    label: function(context) {
                                                        const label = context.label || '';
                                                        const value = context.parsed;
                                                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                        const percentage = Math.round((value / total) * 100);
                                                        return `${label}: ${formatCurrency(value, currency, locale)} (${percentage}%)`;
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        
                        <div className="col-lg-6">
                            <h4 className="panel-title">Análise dos Métodos</h4>
                            <div className="payment-analysis">
                                <div className="analysis-item">
                                    <div className="analysis-icon">
                                        <CreditCard size={20} className="text-primary" />
                                    </div>
                                    <div className="analysis-content">
                                        <h6>Método Mais Popular</h6>
                                        <p className="analysis-value">
                                            <strong>{paymentStats.metodoMaisPopular.nome}</strong>
                                            <span className="analysis-meta">
                                                {paymentStats.metodoMaisPopular.count?.toLocaleString('pt-BR')} transações
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="analysis-item">
                                    <div className="analysis-icon">
                                        <DollarSign size={20} className="text-success" />
                                    </div>
                                    <div className="analysis-content">
                                        <h6>Maior Faturamento</h6>
                                        <p className="analysis-value">
                                            <strong>{paymentStats.metodoMaiorFaturamento.nome}</strong>
                                            <span className="analysis-meta">
                                                {formatCurrency(paymentStats.metodoMaiorFaturamento.total || 0, currency, locale)}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="analysis-item">
                                    <div className="analysis-icon">
                                        <TrendingUp size={20} className="text-info" />
                                    </div>
                                    <div className="analysis-content">
                                        <h6>Maior Ticket Médio</h6>
                                        <p className="analysis-value">
                                            <strong>{paymentStats.metodoMaiorTicket.nome}</strong>
                                            <span className="analysis-meta">
                                                {formatCurrency(paymentStats.metodoMaiorTicket.ticketMedio || 0, currency, locale)}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                <div className="analysis-item">
                                    <div className="analysis-icon">
                                        <BarChart3 size={20} className="text-warning" />
                                    </div>
                                    <div className="analysis-content">
                                        <h6>Distribuição por Tipo</h6>
                                        <p className="analysis-value">
                                            <strong>
                                                {paymentStats.distribuicaoTipos.prePagos} Pré-pago / {paymentStats.distribuicaoTipos.posPagos} Pós-pago
                                            </strong>
                                            <span className="analysis-meta">
                                                {formatCurrency(paymentStats.distribuicaoTipos.faturamentoPrePago, currency, locale)} / {formatCurrency(paymentStats.distribuicaoTipos.faturamentoPosPago, currency, locale)}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔥 RESUMO EXECUTIVO */}
            {!isBackendUnavailable && paymentStats && (
                <div className="content-panel mt-4">
                    <h4 className="panel-title">Resumo Executivo - Formas de Pagamento</h4>
                    <div className="dashboard-grid">
                        <KpiCard
                            icon={DollarSign}
                            title="Faturamento Total"
                            value={formatCurrency(paymentStats.totalFaturamento, currency, locale)}
                            subtitle="Valor total recebido"
                        />
                        <KpiCard
                            icon={CreditCard}
                            title="Transações"
                            value={paymentStats.totalTransacoes.toLocaleString('pt-BR')}
                            subtitle="Total de transações"
                        />
                        <KpiCard
                            icon={TrendingUp}
                            title="Ticket Médio"
                            value={formatCurrency(paymentStats.ticketMedioGeral, currency, locale)}
                            subtitle="Valor médio por transação"
                        />
                        <KpiCard
                            icon={PieChart}
                            title="Métodos"
                            value={reportData.length.toLocaleString('pt-BR')}
                            subtitle="Formas de pagamento utilizadas"
                        />
                    </div>
                </div>
            )}

            {/* 🔥 MENSAGEM DE DADOS VAZIOS */}
            {!isBackendUnavailable && !isLoading && reportData.length === 0 && (
                <div className="content-panel mt-4">
                    <div className="empty-state text-center py-5">
                        <CreditCard size={64} className="text-muted mb-3" />
                        <h5>Nenhuma venda encontrada</h5>
                        <p className="text-muted mb-4">
                            Não foram encontradas vendas com pagamentos para os filtros selecionados.
                        </p>
                        <small className="text-muted">
                            Verifique se existem vendas finalizadas com pagamentos no período.
                        </small>
                    </div>
                </div>
            )}
        </ReportPageLayout>
    );
};

export default PagamentosReport;